import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { useEffect, useMemo, useState } from "react";

export default function UserListModal({
  open,
  onClose,
  attendees,
  initiallySelected = [],
  onAddUsers,
}) {
  const [q, setQ] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [selectedMap, setSelectedMap] = useState({});

  useEffect(() => {
    const seeded = {};
    (initiallySelected || []).forEach((u) => (seeded[u.userId] = u));
    setSelectedMap(seeded);
  }, [initiallySelected, open]);

  const normalized = useMemo(() => {
    return (attendees || []).map((a) => {
      const fullName = `${a.name || ""} ${a.lastname || ""}`.trim();
      return {
        userId: a.userId,
        athleteEmail: a.athleteEmail,
        fullName,
      };
    });
  }, [attendees]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = !s
      ? normalized
      : normalized.filter((u) => {
          return (
            u.fullName.toLowerCase().includes(s) ||
            (u.athleteEmail || "").toLowerCase().includes(s)
          );
        });

    list.sort((a, b) => {
      const cmp = a.fullName.localeCompare(b.fullName);
      return sortAsc ? cmp : -cmp;
    });

    return list;
  }, [normalized, q, sortAsc]);

  const selectedCount = Object.keys(selectedMap).length;

  const toggle = (user) => {
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[user.userId]) delete next[user.userId];
      else next[user.userId] = user;
      return next;
    });
  };

  const addUsers = () => {
    onAddUsers(Object.values(selectedMap));
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/40" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-4xl rounded-lg bg-white shadow-xl">
          <div className="p-6">
            <div className="flex items-center justify-between gap-4">
              <DialogTitle className="text-xl font-semibold">
                User List
              </DialogTitle>

              <div className="flex items-center gap-3">
                <div className="relative">
                  <input
                    className="border rounded-md pl-10 pr-3 py-2 w-[320px]"
                    placeholder="Search User"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    🔍
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 border rounded-lg overflow-hidden">
              <div className="grid grid-cols-[60px_1fr_1fr] bg-gray-50 border-b px-4 py-3 text-sm font-semibold text-gray-700">
                <div></div>

                <button
                  type="button"
                  className="flex items-center gap-2 text-left"
                  onClick={() => setSortAsc((v) => !v)}
                >
                  Full Name
                  <span className="text-gray-500">{sortAsc ? "↑" : "↓"}</span>
                </button>

                <div>Email</div>
              </div>

              <div className="max-h-[350px] overflow-y-auto">
                {filtered.map((u) => {
                  const checked = !!selectedMap[u.userId];
                  return (
                    <div
                      key={u.userId}
                      className="grid grid-cols-[60px_1fr_1fr] px-4 py-3 border-b items-center"
                    >
                      <div>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(u)}
                        />
                      </div>

                      <div className="flex items-center gap-3">
                        <span className="text-gray-700">👤</span>
                        <span className="text-gray-900">{u.fullName}</span>
                      </div>

                      <div className="text-gray-700">{u.athleteEmail}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 text-sm text-gray-700">
              {String(selectedCount).padStart(2, "0")} User
            </div>
          </div>

          <div className="p-6 pt-0 flex justify-end gap-4">
            <button
              className="px-6 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              onClick={onClose}
              type="button"
            >
              Close
            </button>

            <button
              className="px-6 py-2 rounded bg-red-500 text-white hover:bg-red-700 disabled:opacity-50"
              onClick={addUsers}
              type="button"
              disabled={selectedCount === 0}
            >
              Add User
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
